import { useState, useRef } from 'react';
import { Plus, Upload, Loader2, Trash2, Music, Edit, FileAudio, Send, Calendar, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';
import { API, API_BASE } from '../../config/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { PreviewModeToggle } from './PreviewModeToggle';

const EMPTY_FORM = {
  title: '', artist: '', description: '', cover_image: '', preview_url: '', download_file: '',
  price: 0, is_downloadable: false, streaming_links: {}, release_date: '', is_upcoming: false,
  presave_link: '', album_id: '', featured_artists: [], isrc_code: '', upc_code: '',
  scheduled_release_at: '', lyrics: '', sale_price: '', on_sale: false, purchasable: false,
};

const EMPTY_BULK_DEFAULTS = { artist: '', price: 0, album_id: '', is_downloadable: false, cover_image: '' };

export function TracksPanel({ tracks, setTracks, albums, onRefresh }) {
  // ── Form / dialog state ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [releasingTrack, setReleasingTrack] = useState(null);
  const coverInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // ── Bulk upload state ──
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkDefaults, setBulkDefaults] = useState(EMPTY_BULK_DEFAULTS);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadingCover, setBulkUploadingCover] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const bulkFileRef = useRef(null);
  const bulkCoverRef = useRef(null);

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingTrack(null); };

  const openEditDialog = (track) => {
    setEditingTrack(track);
    setFormData({ ...EMPTY_FORM, ...track, streaming_links: track.streaming_links || {} });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.sale_price === '' || payload.sale_price === undefined) payload.sale_price = null;
      if (payload.price === '') payload.price = null;
      if (editingTrack) {
        await api.put(`${API}/tracks/${editingTrack.id}`, payload, { withCredentials: true });
        toast.success('Track updated!');
      } else {
        await api.post(`${API}/tracks`, payload, { withCredentials: true });
        toast.success('Track created!');
      }
      setDialogOpen(false);
      resetForm();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save track');
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) return;
    try {
      await api.delete(`${API}/tracks/${trackId}`, { withCredentials: true });
      toast.success('Track deleted!');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete track');
    }
  };

  const extractTitle = (filename) => {
    const name = filename.replace(/\.[^/.]+$/, '');
    return name.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
  };

  const handleBulkFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const entries = files.filter(f => /\.(mp3|wav|flac|m4a)$/i.test(f.name)).map(f => ({
      file: f, title: extractTitle(f.name), audioUrl: '', uploaded: false,
    }));
    if (!entries.length) { toast.error('No valid audio files selected'); return; }
    setBulkFiles(prev => [...prev, ...entries]);
  };

  const handleBulkDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    const entries = files.filter(f => /\.(mp3|wav|flac|m4a)$/i.test(f.name)).map(f => ({
      file: f, title: extractTitle(f.name), audioUrl: '', uploaded: false,
    }));
    if (!entries.length) { toast.error('No valid audio files found'); return; }
    setBulkFiles(prev => [...prev, ...entries]);
  };

  const handleBulkCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploadingCover(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post(`${API}/upload/cover`, fd, { withCredentials: true });
      setBulkDefaults(prev => ({ ...prev, cover_image: data.url }));
      toast.success('Cover uploaded');
    } catch { toast.error('Failed to upload cover'); }
    setBulkUploadingCover(false);
  };

  const handleBulkSubmit = async () => {
    if (!bulkFiles.length) return;
    if (!bulkDefaults.artist) { toast.error('Artist name is required'); return; }
    setBulkUploading(true);
    const total = bulkFiles.length;
    setBulkProgress({ current: 0, total });
    try {
      const uploaded = [];
      for (let i = 0; i < bulkFiles.length; i++) {
        setBulkProgress({ current: i + 1, total });
        const entry = bulkFiles[i];
        const fd = new FormData(); fd.append('file', entry.file);
        const { data } = await api.post(`${API}/upload/audio`, fd, { withCredentials: true });
        uploaded.push({ ...entry, audioUrl: data.url, uploaded: true });
      }
      const tracksPayload = uploaded.map(entry => ({
        title: entry.title, artist: bulkDefaults.artist, price: bulkDefaults.price || 0,
        is_downloadable: bulkDefaults.is_downloadable, cover_image: bulkDefaults.cover_image || '',
        preview_url: entry.audioUrl, download_file: bulkDefaults.is_downloadable ? entry.audioUrl : '',
        album_id: bulkDefaults.album_id || null, description: '', sale_price: null, on_sale: false, purchasable: false,
      }));
      await api.post(`${API}/tracks/bulk`, { tracks: tracksPayload, album_id: bulkDefaults.album_id || null }, { withCredentials: true });
      toast.success(`${uploaded.length} tracks created!`);
      setBulkUploadOpen(false);
      setBulkFiles([]);
      setBulkDefaults(EMPTY_BULK_DEFAULTS);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create tracks');
    }
    setBulkUploading(false);
  };

  const updateStreamingLink = (platform, url) => {
    setFormData(prev => ({ ...prev, streaming_links: { ...prev.streaming_links, [platform]: url } }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/cover`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, cover_image: `${API_BASE}${data.url}` }));
      toast.success('Cover image uploaded!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload cover');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post(`${API}/upload/audio`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, preview_url: `${API_BASE}${data.url}`, download_file: data.path }));
      toast.success('Audio file uploaded!');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to upload audio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleReleaseTrack = async (trackId) => {
    if (!window.confirm('Release this track and notify all pre-savers?')) return;
    setReleasingTrack(trackId);
    try {
      const { data } = await api.post(`${API}/tracks/${trackId}/release`, {}, { withCredentials: true });
      toast.success(data.message);
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to release track');
    } finally {
      setReleasingTrack(null);
    }
  };

  return (
            <div className="space-y-6" data-testid="tracks-content">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h2 className="font-cabinet text-xl sm:text-2xl font-black uppercase tracking-tighter">
                  TRACKS
                </h2>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    className="border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white rounded-none font-black text-sm px-5 py-3"
                    onClick={async () => {
                      if (!window.confirm('Set ALL tracks to 30s preview mode (and fill missing unlock prices with $1.99)?\n\nYou can still flip individual tracks back to FULL afterwards.')) return;
                      try {
                        const { data } = await api.post(`${API}/tracks/bulk-set-preview-mode`,
                          { mode: 'snippet', unlock_price: 1.99 }, { withCredentials: true });
                        toast.success(`Set ${data.modified} tracks to 30s preview${data.price_filled ? ` · filled $1.99 on ${data.price_filled}` : ''}`);
                        // Refresh tracks
                        const res = await api.get(`${API}/tracks`);
                        setTracks(res.data);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Bulk update failed');
                      }
                    }}
                    data-testid="bulk-snippet-btn"
                  >
                    SET ALL TO 30s PREVIEW
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E] hover:text-black rounded-none font-black text-sm px-5 py-3"
                    onClick={async () => {
                      if (!window.confirm('Set ALL tracks back to FULL playback (removes 30s preview cap)?')) return;
                      try {
                        const { data } = await api.post(`${API}/tracks/bulk-set-preview-mode`,
                          { mode: 'full' }, { withCredentials: true });
                        toast.success(`Set ${data.modified} tracks to FULL playback`);
                        const res = await api.get(`${API}/tracks`);
                        setTracks(res.data);
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Bulk update failed');
                      }
                    }}
                    data-testid="bulk-full-btn"
                  >
                    SET ALL TO FULL
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520] hover:bg-[#DAA520] hover:text-black rounded-none font-black text-sm px-5 py-3"
                    onClick={() => setBulkUploadOpen(true)}
                    data-testid="bulk-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    BULK UPLOAD
                  </Button>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                  setDialogOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#22C55E] hover:bg-[#22C55E]/80 text-black font-black text-sm px-6 py-3 rounded-none animate-pulse hover:animate-none" data-testid="add-track-btn">
                      <Plus className="w-5 h-5 mr-2" />
                      ADD TRACK
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-cabinet text-xl font-black uppercase">
                        {editingTrack ? 'EDIT TRACK' : 'ADD NEW TRACK'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="overline">Title</Label>
                          <Input
                            value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                            required
                            data-testid="track-title-input"
                          />
                        </div>
                        <div>
                          <Label className="overline">Artist</Label>
                          <Input
                            value={formData.artist}
                            onChange={(e) => setFormData(prev => ({ ...prev, artist: e.target.value }))}
                            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                            required
                            data-testid="track-artist-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="overline">Description</Label>
                        <Input
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                          data-testid="track-description-input"
                        />
                      </div>
                      
                      {/* Cover Image Upload */}
                      <div>
                        <Label className="overline">Cover Image</Label>
                        <div className="mt-2 flex gap-2">
                          <Input
                            value={formData.cover_image}
                            onChange={(e) => setFormData(prev => ({ ...prev, cover_image: e.target.value }))}
                            className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                            placeholder="https://... or upload"
                            data-testid="track-cover-input"
                          />
                          <input
                            type="file"
                            ref={coverInputRef}
                            accept="image/*"
                            onChange={handleCoverUpload}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => coverInputRef.current?.click()}
                            disabled={uploadingCover}
                            className="rounded-none border-[#00B4D8] bg-[#00B4D8]/10 text-[#00B4D8] hover:bg-[#00B4D8] hover:text-black font-bold px-4"
                            data-testid="upload-cover-btn"
                          >
                            {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />UPLOAD</>}
                          </Button>
                        </div>
                        {formData.cover_image && (
                          <div className="mt-2 w-20 h-20 bg-[#262626] overflow-hidden">
                            <img src={formData.cover_image} alt="Cover preview" className="w-full h-full object-cover" />
                          </div>
                        )}
                      </div>
                      
                      {/* Audio File Upload */}
                      <div>
                        <Label className="overline">Full Track Audio (Streaming)</Label>
                        <p className="text-xs text-[#A3A3A3] mb-2">Upload the full track - listeners can stream the complete song</p>
                        <div className="mt-2 flex gap-2">
                          <Input
                            value={formData.preview_url}
                            onChange={(e) => setFormData(prev => ({ ...prev, preview_url: e.target.value }))}
                            className="flex-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                            placeholder="https://... or upload MP3"
                            data-testid="track-preview-input"
                          />
                          <input
                            type="file"
                            ref={audioInputRef}
                            accept="audio/*"
                            onChange={handleAudioUpload}
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => audioInputRef.current?.click()}
                            disabled={uploadingAudio}
                            className="rounded-none border-[#00B4D8] bg-[#00B4D8]/10 text-[#00B4D8] hover:bg-[#00B4D8] hover:text-black font-bold px-4"
                            data-testid="upload-audio-btn"
                          >
                            {uploadingAudio ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileAudio className="w-4 h-4 mr-1" />UPLOAD</>}
                          </Button>
                        </div>
                        {formData.download_file && (
                          <p className="mt-2 text-xs text-[#22C55E] flex items-center gap-1">
                            <FileAudio className="w-3 h-3" /> Full track uploaded - ready for streaming
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="overline">Price (USD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.price}
                            onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                            className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none"
                            data-testid="track-price-input"
                          />
                        </div>
                        <div className="flex items-center gap-3 pt-6">
                          <Switch
                            checked={formData.is_downloadable}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_downloadable: checked }))}
                            data-testid="track-downloadable-switch"
                          />
                          <Label className="overline">Available for Download</Label>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.purchasable}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, purchasable: checked }))}
                          data-testid="track-purchasable-switch"
                        />
                        <div>
                          <Label className="overline">Available for Purchase</Label>
                          <p className="text-xs text-[#A3A3A3]">Show the Buy button on the track. When off, fans can stream but not buy.</p>
                        </div>
                      </div>
                      
                      {/* Sale Pricing */}
                      <div className="border-t border-[#262626] pt-4">
                        <div className="flex items-center justify-between mb-4">
                          <Label className="overline text-[#DAA520]">Sale Pricing</Label>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={formData.on_sale}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, on_sale: checked }))}
                              data-testid="track-on-sale-switch"
                            />
                            <span className="text-xs text-[#A3A3A3]">On Sale</span>
                          </div>
                        </div>
                        {formData.on_sale && (
                          <div className="bg-[#DAA520]/10 p-4 border border-[#DAA520]/30">
                            <Label className="text-xs text-[#A3A3A3]">Sale Price (USD)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.sale_price}
                              onChange={(e) => setFormData(prev => ({ ...prev, sale_price: parseFloat(e.target.value) || '' }))}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="Discounted price"
                              data-testid="track-sale-price-input"
                            />
                            {formData.price > 0 && formData.sale_price && (
                              <p className="text-xs text-[#DAA520] mt-2">
                                {Math.round((1 - formData.sale_price / formData.price) * 100)}% off — ${formData.price.toFixed(2)} → ${parseFloat(formData.sale_price).toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Distribution Codes Section */}
                      <div className="border-t border-[#262626] pt-4">
                        <Label className="overline mb-3 block text-[#FF3B30]">Distribution Codes</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">ISRC Code</Label>
                            <Input
                              value={formData.isrc_code}
                              onChange={(e) => setFormData(prev => ({ ...prev, isrc_code: e.target.value.toUpperCase() }))}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none uppercase"
                              placeholder="USRC12345678"
                              data-testid="track-isrc-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">UPC/EAN Code</Label>
                            <Input
                              value={formData.upc_code}
                              onChange={(e) => setFormData(prev => ({ ...prev, upc_code: e.target.value }))}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="012345678901"
                              data-testid="track-upc-input"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-[#A3A3A3] mt-2">ISRC identifies the track, UPC identifies the release (from your distributor)</p>
                      </div>

                      <div className="border-t border-[#262626] pt-4">
                        <Label className="overline mb-3 block">Lyrics</Label>
                        <textarea
                          value={formData.lyrics}
                          onChange={(e) => setFormData(prev => ({ ...prev, lyrics: e.target.value }))}
                          rows={6}
                          className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#FF3B30] p-3 text-white resize-vertical outline-none font-mono text-sm"
                          placeholder="Paste your song lyrics here..."
                          data-testid="track-lyrics-input"
                        />
                        <p className="text-xs text-[#A3A3A3] mt-1">Lyrics will be available on the homepage via a button on each track</p>
                      </div>
                      
                      <div className="border-t border-[#262626] pt-4">
                        <Label className="overline mb-3 block">Streaming Links</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">Spotify</Label>
                            <Input
                              value={formData.streaming_links.spotify || ''}
                              onChange={(e) => updateStreamingLink('spotify', e.target.value)}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="https://open.spotify.com/..."
                              data-testid="track-spotify-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">Apple Music</Label>
                            <Input
                              value={formData.streaming_links.apple || ''}
                              onChange={(e) => updateStreamingLink('apple', e.target.value)}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="https://music.apple.com/..."
                              data-testid="track-apple-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">SoundCloud</Label>
                            <Input
                              value={formData.streaming_links.soundcloud || ''}
                              onChange={(e) => updateStreamingLink('soundcloud', e.target.value)}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="https://soundcloud.com/..."
                              data-testid="track-soundcloud-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-[#A3A3A3]">YouTube</Label>
                            <Input
                              value={formData.streaming_links.youtube || ''}
                              onChange={(e) => updateStreamingLink('youtube', e.target.value)}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="https://youtube.com/..."
                              data-testid="track-youtube-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Pre-save / Upcoming Release Section */}
                      <div className="border-t border-[#262626] pt-4">
                        <div className="flex items-center justify-between mb-4">
                          <Label className="overline">Upcoming Release (Pre-save)</Label>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={formData.is_upcoming}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_upcoming: checked }))}
                              data-testid="track-upcoming-switch"
                            />
                            <span className="text-xs text-[#A3A3A3]">Enable Pre-save</span>
                          </div>
                        </div>
                        
                        {formData.is_upcoming && (
                          <div className="grid grid-cols-2 gap-4 bg-[#FF3B30]/10 p-4 border border-[#FF3B30]/30">
                            <div>
                              <Label className="text-xs text-[#A3A3A3]">Release Date</Label>
                              <Input
                                type="date"
                                value={formData.release_date || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, release_date: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                                data-testid="track-release-date-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#A3A3A3]">Spotify Pre-save Link</Label>
                              <Input
                                value={formData.presave_link || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, presave_link: e.target.value }))}
                                className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                                placeholder="https://distrokid.com/hyperfollow/..."
                                data-testid="track-presave-link-input"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button
                          type="submit"
                          className="flex-1 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none"
                          data-testid="track-submit-btn"
                        >
                          {editingTrack ? 'UPDATE TRACK' : 'ADD TRACK'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                          className="rounded-none border-[#262626]"
                        >
                          CANCEL
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              </div>

              {/* Bulk Upload Dialog */}
              <Dialog open={bulkUploadOpen} onOpenChange={(open) => {
                if (!open && !bulkUploading) {
                  setBulkUploadOpen(false);
                  setBulkFiles([]);
                  setBulkDefaults({ artist: '', price: 0, album_id: '', is_downloadable: false, cover_image: '' });
                }
              }}>
                <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-cabinet text-xl font-black uppercase">
                      BULK UPLOAD TRACKS
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div
                      className="border-2 border-dashed border-[#333] hover:border-[#DAA520] p-8 text-center cursor-pointer transition-colors"
                      onClick={() => bulkFileRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleBulkDrop}
                      data-testid="bulk-drop-zone"
                    >
                      <Upload className="w-8 h-8 mx-auto text-[#666] mb-2" />
                      <p className="text-[#A3A3A3] text-sm">Drag & drop audio files here, or click to select</p>
                      <p className="text-[#666] text-xs mt-1">MP3, WAV, FLAC, M4A</p>
                      <input ref={bulkFileRef} type="file" accept=".mp3,.wav,.flac,.m4a" multiple onChange={handleBulkFilesSelected} className="hidden" />
                    </div>

                    {bulkFiles.length > 0 && (
                      <div className="space-y-2">
                        <Label className="overline">Tracks ({bulkFiles.length})</Label>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {bulkFiles.map((entry, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] p-2">
                              <FileAudio className="w-4 h-4 text-[#DAA520] shrink-0" />
                              <Input
                                value={entry.title}
                                onChange={(e) => {
                                  const updated = [...bulkFiles];
                                  updated[idx].title = e.target.value;
                                  setBulkFiles(updated);
                                }}
                                className="bg-transparent border-none text-sm h-7 p-1"
                                data-testid={`bulk-track-title-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={() => setBulkFiles(prev => prev.filter((_, i) => i !== idx))}
                                className="text-[#666] hover:text-[#FF3B30] shrink-0"
                                data-testid={`bulk-remove-${idx}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {bulkFiles.length > 0 && (
                      <div className="space-y-3 border-t border-[#262626] pt-4">
                        <Label className="overline text-[#DAA520]">Shared Settings (applied to all)</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="overline text-xs">Artist *</Label>
                            <Input
                              value={bulkDefaults.artist}
                              onChange={(e) => setBulkDefaults(prev => ({ ...prev, artist: e.target.value }))}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              placeholder="Artist name"
                              data-testid="bulk-artist-input"
                            />
                          </div>
                          <div>
                            <Label className="overline text-xs">Price ($)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={bulkDefaults.price}
                              onChange={(e) => setBulkDefaults(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                              className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none"
                              data-testid="bulk-price-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="overline text-xs">Album (optional)</Label>
                          <select
                            value={bulkDefaults.album_id}
                            onChange={(e) => setBulkDefaults(prev => ({ ...prev, album_id: e.target.value }))}
                            className="w-full mt-1 bg-[#0A0A0A] border border-[#262626] text-white p-2 text-sm"
                            data-testid="bulk-album-select"
                          >
                            <option value="">No album</option>
                            {albums.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="overline text-xs">Shared Cover Image</Label>
                          <div className="flex items-center gap-2 mt-1">
                            {bulkDefaults.cover_image && (
                              <img src={`${API_BASE}${bulkDefaults.cover_image}`} alt="" className="w-10 h-10 object-cover border border-[#262626]" />
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="rounded-none border-[#262626] text-xs"
                              onClick={() => bulkCoverRef.current?.click()}
                              disabled={bulkUploadingCover}
                              data-testid="bulk-cover-btn"
                            >
                              {bulkUploadingCover ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ImageIcon className="w-3 h-3 mr-1" />}
                              {bulkDefaults.cover_image ? 'Change' : 'Upload Cover'}
                            </Button>
                            <input ref={bulkCoverRef} type="file" accept="image/*" onChange={handleBulkCoverUpload} className="hidden" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={bulkDefaults.is_downloadable}
                            onCheckedChange={(v) => setBulkDefaults(prev => ({ ...prev, is_downloadable: v }))}
                            data-testid="bulk-downloadable-switch"
                          />
                          <Label className="text-sm">Downloadable</Label>
                        </div>
                      </div>
                    )}

                    {bulkUploading && (
                      <div className="bg-[#0A0A0A] border border-[#262626] p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Loader2 className="w-4 h-4 animate-spin text-[#DAA520]" />
                          <span className="text-sm text-[#A3A3A3]">Uploading {bulkProgress.current} of {bulkProgress.total}...</span>
                        </div>
                        <div className="w-full bg-[#262626] h-1.5">
                          <div className="bg-[#DAA520] h-1.5 transition-all" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }} />
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 justify-end pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none border-[#262626]"
                        onClick={() => { setBulkUploadOpen(false); setBulkFiles([]); }}
                        disabled={bulkUploading}
                        data-testid="bulk-cancel-btn"
                      >
                        CANCEL
                      </Button>
                      <Button
                        type="button"
                        className="bg-[#DAA520] hover:bg-[#FFD700] text-black rounded-none font-bold"
                        onClick={handleBulkSubmit}
                        disabled={bulkUploading || !bulkFiles.length}
                        data-testid="bulk-submit-btn"
                      >
                        {bulkUploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />UPLOADING...</> : `CREATE ${bulkFiles.length} TRACK${bulkFiles.length !== 1 ? 'S' : ''}`}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {tracks.length === 0 ? (
                <div className="bg-[#141414] border border-[#262626] p-12 text-center">
                  <Music className="w-12 h-12 mx-auto text-[#A3A3A3] mb-4" />
                  <p className="text-[#A3A3A3]">No tracks yet. Add your first track!</p>
                </div>
              ) : (
                <div className="bg-[#141414] border border-[#262626] table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>Order</th>
                        <th>Track</th>
                        <th>Artist</th>
                        <th>Status</th>
                        <th>Price</th>
                        <th>Plays</th>
                        <th>Preview Mode</th>
                        <th>Pre-saves</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracks.map((track, idx) => (
                        <tr key={track.id} data-testid={`admin-track-${track.id}`}>
                          <td>
                            <div className="flex flex-col gap-0.5 items-center">
                              <button
                                onClick={async () => {
                                  if (idx === 0) return;
                                  const reordered = [...tracks];
                                  [reordered[idx - 1], reordered[idx]] = [reordered[idx], reordered[idx - 1]];
                                  setTracks(reordered);
                                  try {
                                    await api.put(`${API}/tracks/reorder`, { track_ids: reordered.map(t => t.id) }, { withCredentials: true });
                                  } catch { toast.error('Reorder failed'); }
                                }}
                                disabled={idx === 0}
                                className="text-[#666] hover:text-[#DAA520] disabled:opacity-30"
                                title="Move up"
                                data-testid={`track-up-${track.id}`}
                              >▲</button>
                              <button
                                onClick={async () => {
                                  if (idx === tracks.length - 1) return;
                                  const reordered = [...tracks];
                                  [reordered[idx + 1], reordered[idx]] = [reordered[idx], reordered[idx + 1]];
                                  setTracks(reordered);
                                  try {
                                    await api.put(`${API}/tracks/reorder`, { track_ids: reordered.map(t => t.id) }, { withCredentials: true });
                                  } catch { toast.error('Reorder failed'); }
                                }}
                                disabled={idx === tracks.length - 1}
                                className="text-[#666] hover:text-[#DAA520] disabled:opacity-30"
                                title="Move down"
                                data-testid={`track-down-${track.id}`}
                              >▼</button>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-3">
                              {track.cover_image && (
                                <img src={track.cover_image} alt="" className="w-10 h-10 object-cover" />
                              )}
                              <span className="font-medium">{track.title}</span>
                              <button
                                onClick={async () => {
                                  try {
                                    const { data } = await api.put(`${API}/tracks/home-featured/${track.id}`);
                                    setTracks(tracks.map(t => t.id === track.id
                                      ? { ...t, home_featured: data.featured, home_featured_order: data.featured ? data.count - 1 : undefined }
                                      : t));
                                    toast.success(data.featured
                                      ? `★ Featured on homepage (${data.count}/6)`
                                      : `☆ Removed from homepage`);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Toggle failed');
                                  }
                                }}
                                title={track.home_featured ? `Featured on homepage (#${(track.home_featured_order ?? 0) + 1}/6) — click to remove` : 'Show on homepage (max 6)'}
                                className={`shrink-0 text-base leading-none transition-colors ${track.home_featured ? 'text-[#DAA520] hover:text-[#FFD700]' : 'text-[#404040] hover:text-[#DAA520]'}`}
                                data-testid={`track-home-feature-${track.id}`}
                              >
                                {track.home_featured ? '★' : '☆'}
                              </button>
                            </div>
                          </td>
                          <td className="text-[#A3A3A3]">{track.artist}</td>
                          <td>
                            {track.is_upcoming ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF3B30]/20 text-[#FF3B30] text-xs font-mono">
                                <Calendar className="w-3 h-3" />
                                UPCOMING
                              </span>
                            ) : (
                              <span className="text-[#22C55E] text-xs font-mono">RELEASED</span>
                            )}
                          </td>
                          <td className="font-mono">
                            {track.is_downloadable ? (
                              <>
                                {track.on_sale && track.sale_price != null ? (
                                  <span className="flex items-center gap-1.5">
                                    <span className="line-through text-[#A3A3A3] text-xs">${track.price.toFixed(2)}</span>
                                    <span className="text-[#DAA520] font-bold">${track.sale_price.toFixed(2)}</span>
                                  </span>
                                ) : `$${track.price.toFixed(2)}`}
                              </>
                            ) : track.price > 0 ? `$${track.price.toFixed(2)}` : '-'}
                            {track.purchasable ? (
                              <span className="block text-[10px] text-[#22C55E] uppercase mt-0.5">Purchasable</span>
                            ) : (
                              <span className="block text-[10px] text-[#A3A3A3] uppercase mt-0.5">Not for Sale</span>
                            )}
                          </td>
                          <td className="font-mono">{track.plays || 0}</td>
                          <td>
                            <PreviewModeToggle
                              value={{ preview_mode: track.preview_mode, unlock_price: track.unlock_price }}
                              onChange={async ({ preview_mode, unlock_price }) => {
                                try {
                                  const { data } = await api.put(`${API}/tracks/${track.id}`,
                                    { preview_mode, unlock_price }, { withCredentials: true });
                                  setTracks(tracks.map(t => t.id === track.id ? { ...t, preview_mode: data.preview_mode, unlock_price: data.unlock_price } : t));
                                  toast.success(`Saved · ${preview_mode === 'snippet' ? '30s preview' : 'full track'}`);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Save failed');
                                  throw err;
                                }
                              }}
                              testid={`track-preview-${track.id}`}
                            />
                          </td>
                          <td className="font-mono">
                            {track.is_upcoming ? (
                              <span className="text-[#FF3B30]">{track.presave_count || 0}</span>
                            ) : (
                              <span className="text-[#A3A3A3]">-</span>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-1">
                              <button onClick={() => openEditDialog(track)}
                                className="px-3 py-1.5 text-xs font-bold uppercase bg-[#00B4D8] hover:bg-[#00B4D8]/80 text-black transition-all"
                                data-testid={`edit-track-${track.id}`}>
                                <Edit className="w-3.5 h-3.5 inline mr-1" />EDIT
                              </button>
                              <button onClick={() => handleDelete(track.id)}
                                className="px-3 py-1.5 text-xs font-bold uppercase bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white transition-all"
                                data-testid={`delete-track-${track.id}`}>
                                <Trash2 className="w-3.5 h-3.5 inline mr-1" />DEL
                              </button>
                              {track.is_upcoming && (
                                <button onClick={() => handleReleaseTrack(track.id)}
                                  className="px-3 py-1.5 text-xs font-bold uppercase bg-[#22C55E] hover:bg-[#22C55E]/80 text-black transition-all"
                                  data-testid={`release-track-${track.id}`}
                                  disabled={releasingTrack === track.id}>
                                  {releasingTrack === track.id ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : <Send className="w-3.5 h-3.5 inline mr-1" />}
                                  RELEASE
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

  );
}
